import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalHeader,
  HStack,
  Textarea,
  IconButton,
  Icon,
  Box,
  VStack,
  Text,
  Flex,
  Button,
} from "@chakra-ui/react";
import React, { useState, useEffect } from "react";
import { FaTimes } from "react-icons/fa";
import { RiAddLine } from "react-icons/ri";
import { useKGBatchContext } from "context/batch/KGBatchContext";

interface QnaActionProps {
  isOpen: boolean;
  onClose: () => void;
  stepId?: string; // The step ID to associate QnA actions with
}

const QnaAction: React.FC<QnaActionProps> = ({ isOpen, onClose, stepId }) => {
  const [currentQuestion, setCurrentQuestion] = useState<string>("");
  const [questions, setQuestions] = useState<string[]>([]);
  const {
    addQnAAction,
    getQnAActionsForStep,
    selectedStepIndex,
    analysisActionRequests,
  } = useKGBatchContext();

  // Get the current step ID from the selected step
  const currentStepId =
    stepId ||
    (selectedStepIndex !== null
      ? analysisActionRequests[selectedStepIndex]?.step_id
      : undefined);

  // Load existing questions when the modal opens or step changes
  useEffect(() => {
    if (isOpen && currentStepId) {
      const existingActions = getQnAActionsForStep(currentStepId);
      if (existingActions.length > 0) {
        // Get questions from the first QnA action (assuming one QnA action per step for now)
        const firstAction = existingActions[0];
        setQuestions(firstAction.qna_request?.questions || []);
      } else {
        setQuestions([]);
      }
    }
  }, [isOpen, currentStepId]);

  const handleAddQuestion = () => {
    if (currentQuestion.trim()) {
      setQuestions([...questions, currentQuestion.trim()]);
      setCurrentQuestion("");
    }
  };

  const handleRemoveQuestion = (index: number) => {
    const updatedQuestions = [...questions];
    updatedQuestions.splice(index, 1);
    setQuestions(updatedQuestions);
  };

  const handleSaveQuestions = async () => {
    if (!currentStepId) {
      return;
    }
    await addQnAAction(currentStepId, questions);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent maxH="90vh" display="flex" flexDirection="column">
        <ModalHeader>Add Questions</ModalHeader>
        <ModalCloseButton />
        <ModalBody
          overflow="hidden"
          display="flex"
          flexDirection="column"
          pb={6}
        >
          {/* Questions Display Area */}
          <Box
            flex="1"
            minH="240px"
            maxH="50vh"
            mb={4}
            p={3}
            bg="bg.surface"
            border="1px"
            borderColor="gray.200"
            borderRadius="md"
            overflow="auto"
          >
            {questions.length > 0 ? (
              <VStack spacing={2} align="stretch">
                {questions.map((question, index) => (
                  <Flex
                    key={index}
                    bg="white"
                    p={2}
                    borderRadius="md"
                    border="1px"
                    borderColor="gray.200"
                    justify="space-between"
                    align="center"
                  >
                    <Text fontSize="sm" flex="1">
                      {question}
                    </Text>
                    <IconButton
                      icon={<Icon as={FaTimes} boxSize={4} color="red.400" />}
                      aria-label="Remove Question"
                      size="sm"
                      variant="ghost"
                      onClick={() => handleRemoveQuestion(index)}
                    />
                  </Flex>
                ))}
              </VStack>
            ) : (
              <Flex h="100%" align="center" justify="center">
                <Text color="gray.500" fontSize="sm">
                  No questions added yet
                </Text>
              </Flex>
            )}
          </Box>

          {/* Input Area */}
          <HStack>
            <Textarea
              placeholder="Enter any plan specific questions here"
              resize="none"
              minH="60px"
              maxH="100px"
              flex={1}
              borderColor="gray.200"
              focusBorderColor="purple.300"
              fontSize="sm"
              value={currentQuestion}
              onChange={(e) => setCurrentQuestion(e.target.value)}
            />
            <IconButton
              icon={<Icon as={RiAddLine} boxSize={4} color="purple.400" />}
              aria-label="Add Question"
              borderRadius="full"
              size="sm"
              bg="purple.50"
              _hover={{ bg: "purple.100", color: "gray.700" }}
              onClick={handleAddQuestion}
            />
          </HStack>

          <Flex justify="flex-end" mt={4}>
            <Button
              colorScheme="purple"
              size="sm"
              onClick={handleSaveQuestions}
            >
              Save Questions
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default QnaAction;
